import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { api } from "../utils/api";
import { t } from "../utils/i18n";

type Person = { id: string; name: string; avatar_url?: string; points: number };

const initials = (name?: string) => (name || "?").trim().split(" ").map(n => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

// Créditer manuellement des points fidélité à un membre ou abonné du lieu (voir POST
// /venues/:id/engagement/:userId/credit) — recherche parmi membres ET abonnés (les deux
// accumulent déjà des points, voir venue_engagement), sélection unique. Port fidèle de
// OLTODO/components/CreditPointsModal.tsx.
export default function CreditPointsModal({
  venueId, venueSubtype, lang, onClose,
}: {
  venueId: string; venueSubtype?: string; lang: string; onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Person[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Person | null>(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      const calls = [api.get(`/venues/${venueId}/followers?q=${encodeURIComponent(query)}`).catch(() => ({ followers: [] }))];
      if (venueSubtype && venueSubtype !== "lieu") {
        calls.push(api.get(`/venues/${venueId}/members?q=${encodeURIComponent(query)}`).catch(() => ({ members: [] })));
      }
      Promise.all(calls).then(([followersData, membersData]: any[]) => {
        const byId = new Map<string, Person>();
        (followersData?.followers || []).forEach((f: any) => byId.set(f.id, { id: f.id, name: f.name, avatar_url: f.avatar_url, points: f.points || 0 }));
        (membersData?.members || []).forEach((m: any) => byId.set(m.user_id, { id: m.user_id, name: m.name, avatar_url: m.avatar_url, points: m.points || 0 }));
        setResults(Array.from(byId.values()));
      }).finally(() => setSearching(false));
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, venueId, venueSubtype]);

  const submit = () => {
    const n = parseInt(amount, 10);
    if (!selected || !Number.isInteger(n) || n <= 0) return;
    setSubmitting(true);
    api.post(`/venues/${venueId}/engagement/${selected.id}/credit`, { amount: n, note: note.trim() || undefined })
      .then(data => {
        if (data?.engagement) { alert(t(lang, "credit_points_success")); onClose(); }
        else alert(data?.error || "Erreur lors du crédit");
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSubmitting(false));
  };

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>{t(lang, "credit_points_modal_title")}</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
        </div>

        {!selected ? (
          <>
            <input
              value={query} onChange={e => setQuery(e.target.value)}
              placeholder={t(lang, "credit_points_search_placeholder")}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 4 }}
            />
            {searching && <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>Recherche…</div>}
            {!searching && results.map(p => (
              <div key={p.id} onClick={() => setSelected(p)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F5F5F5", cursor: "pointer" }}>
                {p.avatar_url ? <img src={p.avatar_url} alt="" style={{ width: 36, height: 36, borderRadius: 18, objectFit: "cover" }} /> : (
                  <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#666" }}>{initials(p.name)}</div>
                )}
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                <span style={{ fontSize: 12, color: "#999" }}>{p.points} pts</span>
              </div>
            ))}
          </>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F5F5F5", marginBottom: 16 }}>
              {selected.avatar_url ? <img src={selected.avatar_url} alt="" style={{ width: 36, height: 36, borderRadius: 18, objectFit: "cover" }} /> : (
                <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#666" }}>{initials(selected.name)}</div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{selected.name}</div>
                <div style={{ fontSize: 12, color: "#999" }}>{selected.points} pts</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#0a0a0a" }}>Changer</button>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>{t(lang, "credit_points_amount_label")}</div>
            <input
              value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="100" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 12 }}
            />
            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>{t(lang, "credit_points_note_label")}</div>
            <input
              value={note} onChange={e => setNote(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 16 }}
            />
            <button
              onClick={submit} disabled={submitting || !amount}
              style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: submitting || !amount ? 0.6 : 1 }}
            >
              {submitting ? "..." : t(lang, "credit_points_submit")}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
